import { AIModel } from './ai';
import { g17ecoApi, transformResponse } from './g17ecoApi';

// Tags for cache invalidation
const BATCH_TAG = 'ai-supervisor-batch';
const BATCH_STATUS_TAG = 'ai-supervisor-batch-status';
const BATCH_RESULTS_TAG = 'ai-supervisor-batch-results';
const APPROVED_ANSWERS_TAG = 'ai-supervisor-approved-answers';
const TAGS = [BATCH_TAG, BATCH_STATUS_TAG, BATCH_RESULTS_TAG, APPROVED_ANSWERS_TAG];

// Document metadata for efficient lookup
export interface DocumentMetadata {
  fileName: string;
  signedUrl: string;
  mimeType: string;
  uploadedAt: string;
}

// Types based on API specification
export interface SubmitBatchRequest {
  documentIds?: string[];
  model?: AIModel;
}

export interface SubmitBatchResponse {
  batchId: string;
  status: string;
  message: string;
}

export interface BatchProgress {
  total: number;
  retrieved: number;
  answered: number;
  cited: number;
  validated: number;
  qualityChecked: number;
  synthesized: number;
  reconciled: number;
  completed: number;
  failed: number;
  startTime: string;
  lastUpdate?: string;
}

export interface BatchSummary {
  successCount: number;
  failedCount: number;
  avgQualityScore?: number;
  avgProcessingTime?: number;
}

// Real-time status counts from server
export interface StatusCounts {
  pending: number;
  processing: number;
  completed: number;
  failed: number;
}

// Processing statistics computed by server
export interface ProcessingStats {
  avgProcessingTime: number;
  estimatedTimeRemaining: number;
  lastQuestionCompletedAt?: string;
  throughput?: number;
}

// Structured error information
export interface StructuredError {
  code: string;
  message: string;
  agent: string;
  retryable: boolean;
  context?: Record<string, unknown>;
}

export type BatchStatus = 'processing' | 'pausing' | 'paused' | 'completed' | 'failed' | 'cancelled';

enum AISupervisorJobType {
  SurveyAutoAnswer = 'survey_auto_answer',
}

export interface BatchStatusResponse {
  batchId: string;
  jobType: AISupervisorJobType;
  status: BatchStatus;
  progress: BatchProgress;
  summary?: BatchSummary;
  createdAt: string;
  completedAt?: string;
  itemCount: number;
  questionStatuses?: Record<string, string>;
  documentMetadata?: Record<string, DocumentMetadata>;
}

export interface Citation {
  claim: string;
  confidence: number;
  sourceChunk: RetrievedChunk;
  supportingText: string;
}

export interface RetrievedChunk {
  extractId: string;
  chunk: string;
  chunkIndex: number;
  pageStart?: number;
  pageEnd?: number;
  externalDocumentId: string;
  similarity: number;
}

/**
 * Answer data structure used by both SSE events and batch results.
 * Contains the generated answer along with reasoning and metadata.
 * Note: Citations are provided separately in the data.citations array (full Citation objects).
 */
export interface AnswerData {
  value: string; // The actual answer text
  reasoning: string; // Explanation of how the answer was derived
  confidence: string; // Confidence level: "High", "Medium", or "Low"
  completeness: string; // Assessment of answer completeness
  assumptions: string[]; // Assumptions made during answer generation
}

/**
 * Batch processing result for a single question.
 * Contains the final answer, citations, and quality metrics.
 */
export interface BatchResult {
  utrvId: string;
  status: 'completed' | 'failed';
  data: {
    answer: AnswerData; // Same structure as SSE AnswerData
    citations: Citation[]; // Full citation objects (documentId, quote, relevanceScore, etc.)
    retrievedChunks?: RetrievedChunk[]; // Source chunks used for the answer
  };
  qualityScore: number;
  confidenceScore: number;
  validationStatus: 'valid' | 'invalid' | 'warning';
}

interface BatchResultsResponse {
  success: true;
  batchId: string;
  jobType: string;
  job: {
    results: BatchResult[];
    summary: BatchSummary;
    metadata?: {
      documentMetadata?: Record<string, DocumentMetadata>;
      [key: string]: unknown;
    };
  };
}

export interface ApproveResultsRequest {
  resultIds?: string[];
}

export interface ApproveResultsResponse {
  success: true;
  message: string;
  approvedCount: number;
  batchId: string;
}

export interface ApplyToSurveyRequest {
  approvedAnswerIds?: string[];
  overwriteExisting?: boolean;
}

export interface ApplyToSurveyResponse {
  success: true;
  message: string;
  appliedCount: number;
  skippedCount: number;
  errorCount: number;
  errors?: string[];
  batchId: string;
  jobType: string;
}

export type BatchControlResponse = {
  message: string;
  batchId: string;
  jobType: string;
};

export interface BatchJob {
  _id: string;
  config: { settings: { model?: AIModel } };
  batchId: string;
  jobType: string;
  status: BatchStatus;
  createdAt: string;
  completedAt?: string;
  summary?: BatchSummary;
}

export interface ListJobsResponse {
  success: boolean;
  jobs: BatchJob[];
}

export interface ApprovedAnswer {
  _id: string;
  questionId: string;
  batchId: string;
  utrvId: string;
  answerValue: number | string | boolean;
  answerUnit?: string;
  confidenceScore: number;
  qualityScore: number;
  approvedBy: string;
  approvedAt: string;
  metadata: {
    reasoning: string;
    citations: string[];
    validationStatus: string;
    processingTime: number;
  };
  createdAt: string;
}

export interface ApprovedAnswersResponse {
  success: true;
  answers: ApprovedAnswer[];
}

// SSE Event Types
export interface SSEConnectedEvent {
  type: 'connected';
  batchId: string;
  jobType: AISupervisorJobType;
  currentState: BatchStatusResponse;
  timestamp: string;
}

export interface SSEProgressEvent {
  type: 'progress';
  batchId: string;
  jobType: AISupervisorJobType;
  progress: BatchProgress;
  status: 'processing' | 'paused' | 'completed' | 'failed'; // Now required
  counts: StatusCounts; // NEW - server-computed status counts
  stats?: ProcessingStats; // NEW - processing statistics
  model?: string; // AI model used for processing
  timestamp: string;
}

export interface SSECompletedEvent {
  type: 'completed';
  batchId: string;
  jobType: AISupervisorJobType;
  summary: BatchSummary | null;
  progress?: BatchProgress;
  documentCount?: number;
  documentMetadata?: Record<string, DocumentMetadata>;
  timestamp: string;
}

export interface SSEPausedEvent {
  type: 'paused';
  batchId: string;
  jobType: AISupervisorJobType;
  summary?: BatchSummary | null;
  progress?: BatchProgress;
  documentCount?: number;
  documentMetadata?: Record<string, DocumentMetadata>;
  timestamp: string;
}

export interface SSEStatusChangedEvent {
  type: 'status-changed';
  batchId: string;
  jobType: AISupervisorJobType;
  status: BatchStatus;
  previousStatus: BatchStatus;
  reason: 'user_requested' | 'completed' | 'failed' | 'error' | 'auto_paused';
  message?: string;
  timestamp: string;
}

export interface SSEFailedEvent {
  type: 'failed';
  batchId: string;
  jobType: AISupervisorJobType;
  summary?: BatchSummary | null;
  progress?: BatchProgress;
  documentCount?: number;
  documentMetadata?: Record<string, DocumentMetadata>;
  error: string;
  errorDetails?: {
    code: string;
    phase: string;
    affectedItems?: string[];
  };
  timestamp: string;
}

export interface SSEQuestionUpdateEvent {
  type: 'question-update';
  batchId: string;
  jobType: AISupervisorJobType;
  utrvId: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  stage?: 'retrieved' | 'answered' | 'cited' | 'validated' | 'quality_checked' | 'reconciled';
  result?: {
    answer: AnswerData;
    citations: Citation[];
    retrievedChunks?: RetrievedChunk[];
    qualityScore: number;
    confidenceScore: number;
    validationStatus: 'valid' | 'invalid' | 'warning';
    processingTime: number;
  };
  error?: StructuredError; // NEW - structured error object
  errorMessage?: string; // Deprecated - string fallback for compatibility
  timestamp: string;
}

export type SSEEvent =
  | SSEConnectedEvent
  | SSEProgressEvent
  | SSEStatusChangedEvent // NEW
  | SSEPausedEvent
  | SSECompletedEvent
  | SSEFailedEvent
  | SSEQuestionUpdateEvent;

// RTK Query API
export const aiSupervisorApi = g17ecoApi
  .enhanceEndpoints({
    addTagTypes: TAGS,
  })
  .injectEndpoints({
    endpoints: (builder) => ({
      // Submit batch
      submitAutoAnswerBatch: builder.mutation<
        SubmitBatchResponse,
        { initiativeId: string; surveyId: string; data: SubmitBatchRequest }
      >({
        transformResponse,
        query: ({ initiativeId, surveyId, data }) => ({
          url: `/initiatives/${initiativeId}/ai-supervisor/survey/${surveyId}/batch/submit`,
          method: 'post',
          data,
        }),
        invalidatesTags: (_result, _error, { surveyId }) => [
          { type: BATCH_TAG, id: surveyId },
        ],
      }),

      // Get batch results
      getBatchResults: builder.query<
        BatchResultsResponse,
        { initiativeId: string; surveyId: string; batchId: string }
      >({
        transformResponse,
        query: ({ initiativeId, batchId }) => ({
          url: `/initiatives/${initiativeId}/ai-supervisor/batch/${batchId}/results`,
          method: 'get',
        }),
        providesTags: (_result, _error, { batchId }) => [
          { type: BATCH_RESULTS_TAG, id: batchId },
        ],
      }),

      // Approve results
      approveResults: builder.mutation<
        ApproveResultsResponse,
        { initiativeId: string; batchId: string; data: ApproveResultsRequest }
      >({
        transformResponse,
        query: ({ initiativeId, batchId, data }) => ({
          url: `/initiatives/${initiativeId}/ai-supervisor/batch/${batchId}/approve`,
          method: 'post',
          data,
        }),
        invalidatesTags: (_result, _error, { batchId }) => [
          { type: BATCH_RESULTS_TAG, id: batchId },
        ],
      }),

      // Apply to survey
      applyToSurvey: builder.mutation<
        ApplyToSurveyResponse,
        { initiativeId: string; batchId: string; data: ApplyToSurveyRequest }
      >({
        transformResponse,
        query: ({ initiativeId, batchId, data }) => ({
          url: `/initiatives/${initiativeId}/ai-supervisor/batch/${batchId}/apply-to-survey`,
          method: 'post',
          data,
        }),
        invalidatesTags: (_result, _error, { batchId }) => [
          { type: BATCH_STATUS_TAG, id: batchId },
          { type: BATCH_RESULTS_TAG, id: batchId },
        ],
      }),

      // Pause batch
      pauseBatch: builder.mutation<
        BatchControlResponse,
        { initiativeId: string; batchId: string; surveyId: string }
      >({
        transformResponse,
        query: ({ initiativeId, batchId }) => ({
          url: `/initiatives/${initiativeId}/ai-supervisor/batch/${batchId}/pause`,
          method: 'post',
        }),
        invalidatesTags: (_result, _error, { batchId, surveyId }) => [
          { type: BATCH_STATUS_TAG, id: batchId },
          { type: BATCH_TAG, id: surveyId },
        ],
      }),

      // Resume batch
      resumeBatch: builder.mutation<
        BatchControlResponse,
        { initiativeId: string; batchId: string; surveyId: string }
      >({
        transformResponse,
        query: ({ initiativeId, batchId }) => ({
          url: `/initiatives/${initiativeId}/ai-supervisor/batch/${batchId}/resume`,
          method: 'post',
        }),
        invalidatesTags: (_result, _error, { batchId, surveyId }) => [
          { type: BATCH_STATUS_TAG, id: batchId },
          { type: BATCH_TAG, id: surveyId },
        ],
      }),

      // Abort batch
      abortBatch: builder.mutation<
        BatchControlResponse,
        { initiativeId: string; batchId: string; surveyId: string }
      >({
        transformResponse,
        query: ({ initiativeId, batchId }) => ({
          url: `/initiatives/${initiativeId}/ai-supervisor/batch/${batchId}/abort`,
          method: 'post',
        }),
        invalidatesTags: (_result, _error, { batchId, surveyId }) => [
          { type: BATCH_STATUS_TAG, id: batchId },
          { type: BATCH_TAG, id: surveyId },
        ],
      }),

      // List jobs by survey
      listJobsBySurvey: builder.query<
        ListJobsResponse,
        { initiativeId: string; surveyId: string; jobType?: string }
      >({
        transformResponse,
        query: ({ initiativeId, surveyId, jobType = AISupervisorJobType.SurveyAutoAnswer }) => ({
          url: `/initiatives/${initiativeId}/ai-supervisor/survey/${surveyId}/jobs`,
          method: 'get',
          params: { jobType },
        }),
        providesTags: (_result, _error, { surveyId }) => [
          { type: BATCH_TAG, id: surveyId },
        ],
      }),
    }),
  });

export const {
  useSubmitAutoAnswerBatchMutation,
  useGetBatchResultsQuery,
  useApproveResultsMutation,
  useApplyToSurveyMutation,
  usePauseBatchMutation,
  useResumeBatchMutation,
  useAbortBatchMutation,
  useListJobsBySurveyQuery,
} = aiSupervisorApi;
